<template>
  <div class="lep-view ler-view">
    <!-- ─── Loading State ─── -->
    <div v-if="loading" class="ler-loading-state">
      <div class="ler-spinner"></div>
      <p>{{ currentNetwork === 'facebook' ? $t('dash.presenceManagement.reviews.loadingFacebook') : $t('dash.presenceManagement.reviews.loading') }}</p>
    </div>

    <!-- ─── Empty State ─── -->
    <div v-else-if="!reviews || reviews.length === 0" class="ler-empty-state">
      <i class="material-icons ler-empty-icon">{{ currentNetwork === 'facebook' ? 'thumb_up' : 'rate_review' }}</i>
      <h4 class="ler-empty-title">{{ currentNetwork === 'facebook' ? $t('dash.presenceManagement.reviews.noRecommendationsTitle') : $t('dash.presenceManagement.reviews.noReviewsTitle') }}</h4>
      <p class="ler-empty-text">{{ currentNetwork === 'facebook' ? $t('dash.presenceManagement.reviews.noRecommendationsText') : $t('dash.presenceManagement.reviews.noReviewsText') }}</p>
    </div>

    <!-- ─── Main Reviews Content ─── -->
    <div v-else class="ler-reviews-container">

      <!-- ─── Top Stats Banner ─── -->
      <div class="ler-stats-card">
        <!-- Google: Average Score Column -->
        <div v-if="currentNetwork !== 'facebook'" class="ler-score-col">
          <div class="ler-score-val">{{ computedAverageRating }}</div>
          <div class="ler-stars-row">
            <i 
              v-for="star in 5" 
              :key="star" 
              class="material-icons ler-star-icon"
              :class="{ 'ler-star-icon--filled': star <= Math.round(computedAverageRating) }"
            >
              {{ getAverageStarIcon(star) }}
            </i>
          </div>
          <div class="ler-score-count">{{ reviews.length }} {{ $t('dash.presenceManagement.reviews.totalReviews') }}</div>
        </div>

        <!-- Facebook: Recommendation Rate Column -->
        <div v-else class="ler-score-col ler-score-col--fb">
          <div class="ler-score-val ler-score-val--fb">
            <i class="material-icons ler-rec-hero-icon">thumb_up</i>
            <span>{{ computedRecommendationRate }}%</span>
          </div>
          <div class="ler-score-sublabel">{{ $t('dash.presenceManagement.reviews.recommendationRateLabel') }}</div>
          <div class="ler-score-count">{{ reviews.length }} {{ $t('dash.presenceManagement.reviews.totalReviews') }}</div>
        </div>

        <!-- Google: Rating Distribution Progress Bars -->
        <div v-if="currentNetwork !== 'facebook'" class="ler-distribution-col">
          <div v-for="starRating in [5, 4, 3, 2, 1]" :key="starRating" class="ler-dist-row">
            <span class="ler-dist-label">{{ starRating }} <i class="material-icons ler-dist-star">star</i></span>
            <div class="ler-dist-bar-bg">
              <div 
                class="ler-dist-bar-fill" 
                :style="{ width: getStarPercentage(starRating) + '%' }"
              ></div>
            </div>
            <span class="ler-dist-count">{{ getStarCount(starRating) }}</span>
          </div>
        </div>

        <!-- Facebook: Positive / Negative Recommendation Progress Bars -->
        <div v-else class="ler-distribution-col ler-distribution-col--fb">
          <!-- Positive Bar -->
          <div class="ler-dist-row">
            <span class="ler-dist-label ler-dist-label--fb">
              <i class="material-icons ler-dist-icon--pos">thumb_up</i>
              <span>{{ $t('dash.presenceManagement.reviews.distPositiveFb') }}</span>
            </span>
            <div class="ler-dist-bar-bg">
              <div 
                class="ler-dist-bar-fill ler-dist-bar-fill--pos" 
                :style="{ width: getRecommendationPercentage('positive') + '%' }"
              ></div>
            </div>
            <span class="ler-dist-count">{{ positiveCount }}</span>
          </div>
          <!-- Negative Bar -->
          <div class="ler-dist-row">
            <span class="ler-dist-label ler-dist-label--fb">
              <i class="material-icons ler-dist-icon--neg">thumb_down</i>
              <span>{{ $t('dash.presenceManagement.reviews.distNegativeFb') }}</span>
            </span>
            <div class="ler-dist-bar-bg">
              <div 
                class="ler-dist-bar-fill ler-dist-bar-fill--neg" 
                :style="{ width: getRecommendationPercentage('negative') + '%' }"
              ></div>
            </div>
            <span class="ler-dist-count">{{ negativeCount }}</span>
          </div>
        </div>

        <!-- Reply Rate Stat Column -->
        <div class="ler-rate-col">
          <div class="ler-rate-circle" :style="rateDonutStyle">
            <span class="ler-rate-percent">{{ computedReplyRate }}%</span>
          </div>
          <div class="ler-rate-label">{{ $t('dash.presenceManagement.reviews.replyRate') }}</div>
        </div>
      </div>

      <!-- ─── Filters & Search Bar ─── -->
      <div class="ler-toolbar">
        <div class="ler-filters-group">
          <button 
            type="button" 
            class="ler-filter-btn" 
            :class="{ 'ler-filter-btn--active': activeFilter === 'all' }"
            @click="activeFilter = 'all'"
          >
            {{ currentNetwork === 'facebook' ? $t('dash.presenceManagement.reviews.filterAllFb') : $t('dash.presenceManagement.reviews.filterAll') }} ({{ reviews.length }})
          </button>

          <button 
            type="button" 
            class="ler-filter-btn" 
            :class="{ 'ler-filter-btn--active': activeFilter === 'unanswered' }"
            @click="activeFilter = 'unanswered'"
          >
            {{ $t('dash.presenceManagement.reviews.filterUnanswered') }} ({{ unansweredCount }})
          </button>

          <button 
            type="button" 
            class="ler-filter-btn" 
            :class="{ 'ler-filter-btn--active': activeFilter === 'replied' }"
            @click="activeFilter = 'replied'"
          >
            {{ $t('dash.presenceManagement.reviews.filterReplied') }} ({{ repliedCount }})
          </button>

          <button 
            type="button" 
            class="ler-filter-btn" 
            :class="{ 'ler-filter-btn--active': activeFilter === 'positive' }"
            @click="activeFilter = 'positive'"
          >
            {{ currentNetwork === 'facebook' ? $t('dash.presenceManagement.reviews.filterPositiveFb') : $t('dash.presenceManagement.reviews.filterPositive') }} ({{ positiveCount }})
          </button>

          <button 
            v-if="currentNetwork !== 'facebook'"
            type="button" 
            class="ler-filter-btn" 
            :class="{ 'ler-filter-btn--active': activeFilter === 'neutral' }"
            @click="activeFilter = 'neutral'"
          >
            {{ $t('dash.presenceManagement.reviews.filterNeutral') }} ({{ neutralCount }})
          </button>

          <button 
            type="button" 
            class="ler-filter-btn" 
            :class="{ 'ler-filter-btn--active': activeFilter === 'negative' }"
            @click="activeFilter = 'negative'"
          >
            {{ currentNetwork === 'facebook' ? $t('dash.presenceManagement.reviews.filterNegativeFb') : $t('dash.presenceManagement.reviews.filterNegative') }} ({{ negativeCount }})
          </button>
        </div>

        <!-- Search Input -->
        <div class="ler-search-box">
          <i class="material-icons ler-search-icon">search</i>
          <input 
            type="text" 
            v-model="searchQuery" 
            class="ler-search-input" 
            :placeholder="currentNetwork === 'facebook' ? $t('dash.presenceManagement.reviews.searchPlaceholderFacebook') : $t('dash.presenceManagement.reviews.searchPlaceholder')" 
          />
        </div>
      </div>

      <!-- ─── Reviews List ─── -->
      <div class="ler-list">
        <div 
          v-for="review in filteredReviews" 
          :key="review.name || review.reviewId" 
          class="ler-card"
        >
          <!-- Review Header -->
          <div class="ler-card-header">
            <div class="ler-reviewer-info">
              <img 
                v-if="review.reviewer && review.reviewer.profilePhotoUrl && !avatarErrors[getReviewKey(review)]" 
                :src="formatAvatarUrl(review.reviewer.profilePhotoUrl)" 
                class="ler-reviewer-avatar" 
                alt="Avatar"
                referrerpolicy="no-referrer"
                @error="handleAvatarError(review)"
              />
              <div v-else class="ler-reviewer-avatar-placeholder">
                {{ getInitials(review.reviewer ? review.reviewer.displayName : '') }}
              </div>
              <div class="ler-reviewer-meta">
                <span class="ler-reviewer-name">{{ (review.reviewer && review.reviewer.displayName) || (currentNetwork === 'facebook' ? $t('dash.presenceManagement.reviews.facebookUser') : $t('dash.presenceManagement.reviews.googleUser')) }}</span>
                <span class="ler-review-date">{{ formatDate(review.createTime) }}</span>
              </div>
            </div>

            <!-- Status Badge -->
            <span 
              class="ler-status-badge" 
              :class="review.reviewReply ? 'ler-status-badge--replied' : 'ler-status-badge--pending'"
            >
              <i class="material-icons">{{ review.reviewReply ? 'check_circle' : 'hourglass_empty' }}</i>
              {{ review.reviewReply ? $t('dash.presenceManagement.reviews.replied') : $t('dash.presenceManagement.reviews.pendingReply') }}
            </span>
          </div>

          <!-- Star Rating & Comment (Google) vs Recommendation Pill (Facebook) -->
          <div v-if="currentNetwork !== 'facebook'" class="ler-card-rating">
            <i 
              v-for="star in 5" 
              :key="star" 
              class="material-icons ler-star-icon"
              :class="{ 'ler-star-icon--filled': star <= getStarRating(review) }"
            >
              {{ getStarIcon(star, review) }}
            </i>
          </div>
          <div v-else class="ler-card-recommendation">
            <div 
              v-if="review.recommendationType === 'positive'" 
              class="ler-rec-pill ler-rec-pill--positive"
            >
              <i class="material-icons">thumb_up</i>
              <span>{{ $t('dash.presenceManagement.reviews.recommendsBusiness') }}</span>
            </div>
            <div 
              v-else-if="review.recommendationType === 'negative'" 
              class="ler-rec-pill ler-rec-pill--negative"
            >
              <i class="material-icons">thumb_down</i>
              <span>{{ $t('dash.presenceManagement.reviews.doesNotRecommendBusiness') }}</span>
            </div>
            <div 
              v-else-if="review.hasRating" 
              class="ler-card-rating"
            >
              <i 
                v-for="star in 5" 
                :key="star" 
                class="material-icons ler-star-icon"
                :class="{ 'ler-star-icon--filled': star <= getStarRating(review) }"
              >
                {{ getStarIcon(star, review) }}
              </i>
            </div>
          </div>

          <p class="ler-card-comment" v-if="review.comment">{{ cleanReviewText(review.comment) }}</p>
          <p class="ler-card-comment ler-card-comment--empty" v-else><em>{{ currentNetwork === 'facebook' ? $t('dash.presenceManagement.reviews.emptyCommentFacebook') : $t('dash.presenceManagement.reviews.emptyComment') }}</em></p>

          <!-- Action: Toggle reply box / existing reply -->
          <div class="ler-card-actions">
            <button
              type="button"
              class="ler-btn-toggle-reply"
              :class="{ 'ler-btn-toggle-reply--expanded': isReplyExpanded(review), 'ler-btn-toggle-reply--has-reply': !!review.reviewReply }"
              @click="toggleReply(review)"
            >
              <i class="material-icons">
                {{ review.reviewReply ? (isReplyExpanded(review) ? 'visibility_off' : 'visibility') : 'rate_review' }}
              </i>
              <span>
                {{ isReplyExpanded(review)
                    ? (review.reviewReply ? $t('dash.presenceManagement.reviews.hideReply') : $t('dash.presenceManagement.reviews.hideReplyForm'))
                    : (review.reviewReply ? $t('dash.presenceManagement.reviews.viewReply') : $t('dash.presenceManagement.reviews.writeReplyAction')) }}
              </span>
              <i class="material-icons ler-toggle-chevron">
                {{ isReplyExpanded(review) ? 'expand_less' : 'expand_more' }}
              </i>
            </button>
          </div>

          <!-- Collapsible Reply Section (Existing Reply or Form) -->
          <div v-if="isReplyExpanded(review)" class="ler-card-reply-wrapper">
            <!-- Existing Reply Block -->
            <div v-if="review.reviewReply && !replyingReviewIds.includes(review.name)" class="ler-existing-reply">
              <div class="ler-reply-header">
                <span class="ler-reply-title">
                  <i class="material-icons">reply</i>
                  {{ $t('dash.presenceManagement.reviews.yourReply') }}
                </span>
                <div class="ler-reply-actions">
                  <button type="button" class="ler-btn-icon" @click="startEditReply(review)" :title="$t('dash.presenceManagement.reviews.editReply')">
                    <i class="material-icons">edit</i>
                  </button>
                  <button type="button" class="ler-btn-icon ler-btn-icon--danger" @click="confirmDeleteReply(review)" :title="$t('dash.presenceManagement.reviews.deleteReply')">
                    <i class="material-icons">delete</i>
                  </button>
                </div>
              </div>
              <p class="ler-reply-text">{{ cleanReviewText(review.reviewReply.comment) }}</p>
              <span class="ler-reply-date" v-if="review.reviewReply.updateTime">{{ formatDate(review.reviewReply.updateTime) }}</span>
            </div>

            <!-- Reply Box (Form) -->
            <div v-if="!review.reviewReply || replyingReviewIds.includes(review.name)" class="ler-reply-box">
              <div class="ler-reply-box-header">
                <label class="ler-reply-label">
                  <i class="material-icons">rate_review</i>
                  {{ replyingReviewIds.includes(review.name) ? $t('dash.presenceManagement.reviews.editReplyTitle') : $t('dash.presenceManagement.reviews.writeReplyTitle') }}
                </label>

                <div class="ler-reply-box-actions">
                  <div class="template-trigger-wrapper" v-if="templatesFeatureEnabled">
                    <TemplateTrigger :open="templatePickerReviewName === review.name" @open="action => openReviewTemplatePicker(review, action)">
                      <button type="button" class="ler-btn-template">
                        <i class="fa-regular fa-swatchbook"></i>
                        <span>{{ $t('dash.templates.apply-text') }}</span>
                      </button>
                    </TemplateTrigger>
                    <TemplatePicker
                      v-if="templatePickerReviewName === review.name"
                      :action="templateAction"
                      type="text"
                      :secondary-types="['affix']"
                      :save-data="getDraft(review) ? { data: { text: getReviewTemplateSaveText(review) }, source: 'review-reply' } : null"
                      @apply="template => applyReviewTemplate(review, template)"
                      @saved="templatePickerReviewName = null"
                      @close="templatePickerReviewName = null"
                    />
                  </div>

                  <!-- Generate AI Button -->
                  <button
                    type="button"
                    class="ler-btn-ai"
                    :disabled="aiGeneratingIds.includes(review.name)"
                    @click="onGenerateAiReply(review)"
                  >
                    <span>{{ aiGeneratingIds.includes(review.name) ? $t('dash.presenceManagement.reviews.generatingAi') : $t('dash.presenceManagement.reviews.generateAi') }}</span>
                  </button>
                </div>
              </div>

              <textarea 
                :ref="`replyTextarea-${review.name}`"
                :value="getDraft(review)"
                @input="setDraft(review, $event.target.value)"
                class="ler-reply-textarea" 
                rows="4" 
                :placeholder="$t('dash.presenceManagement.reviews.replyPlaceholder')"
              ></textarea>

              <div class="ler-reply-box-footer">
                <button 
                  v-if="replyingReviewIds.includes(review.name)" 
                  type="button" 
                  class="ler-btn-cancel" 
                  @click="cancelEditReply(review)"
                >
                  {{ $t('dash.presenceManagement.reviews.cancel') }}
                </button>

                <button 
                  type="button" 
                  class="ler-btn-submit" 
                  :disabled="!getDraft(review) || !getDraft(review).trim() || submittingIds.includes(review.name)"
                  @click="onSubmitReply(review)"
                >
                  <i class="material-icons" v-if="!submittingIds.includes(review.name)">send</i>
                  <div class="ler-spinner-small" v-else></div>
                  <span>{{ $t('dash.presenceManagement.reviews.sendReply') }}</span>
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import TemplatePicker from '@/components/dash/shared/TemplatePicker.vue'
import TemplateTrigger from '@/components/dash/shared/TemplateTrigger.vue'
import TemplatesFeatureFlag from '@/components/mixins/TemplatesFeatureFlag.vue'
import { getCaret, insertAtCaret, selectedOrAll, restoreCaret } from '@/utils/textTemplate'

export default {
  name: 'TabReviews',
  mixins: [TemplatesFeatureFlag],
  components: { TemplateTrigger, TemplatePicker },
  props: {
    locationData: {
      type: Object,
      required: true
    },
    locationId: {
      type: String,
      default: ''
    },
    currentNetwork: {
      type: String,
      default: 'google'
    }
  },

  data() {
    return {
      loading: false,
      reviews: [],
      serverRecommendationRate: null,
      activeFilter: 'all',
      templatePickerReviewName: null,
      templateAction: 'apply',
      templateCaret: null,
      searchQuery: '',
      replyDrafts: {},
      expandedReviewIds: [],
      replyingReviewIds: [],
      submittingIds: [],
      aiGeneratingIds: [],
      avatarErrors: {}
    }
  },

  computed: {
    computedAverageRating() {
      if (!this.reviews || this.reviews.length === 0) return 0
      const totalStars = this.reviews.reduce((sum, r) => sum + this.getStarRating(r), 0)
      return (totalStars / this.reviews.length).toFixed(1)
    },

    computedRecommendationRate() {
      const total = this.positiveCount + this.negativeCount
      if (total === 0) return this.serverRecommendationRate !== null ? this.serverRecommendationRate : 0
      return Math.round((this.positiveCount / total) * 100)
    },

    computedReplyRate() {
      if (!this.reviews || this.reviews.length === 0) return 0
      const replied = this.reviews.filter(r => !!r.reviewReply).length
      return Math.round((replied / this.reviews.length) * 100)
    },

    rateDonutStyle() {
      const pct = Math.max(0, Math.min(100, Math.round(this.computedReplyRate || 0)))
      return {
        background: `conic-gradient(#6a1f4c ${pct}%, #e8dff0 ${pct}% 100%)`
      }
    },

    targetId() {
      return this.locationId || this.locationData?.id || this.locationData?.name || this.locationData?.localPageId || ''
    },

    unansweredCount() {
      return this.reviews.filter(r => !r.reviewReply).length
    },

    repliedCount() {
      return this.reviews.filter(r => !!r.reviewReply).length
    },

    positiveCount() {
      if (this.currentNetwork === 'facebook') {
        return this.reviews.filter(r => r.recommendationType === 'positive' || (!r.recommendationType && this.getStarRating(r) >= 4)).length
      }
      return this.reviews.filter(r => this.getStarRating(r) >= 4).length
    },

    neutralCount() {
      if (this.currentNetwork === 'facebook') return 0
      return this.reviews.filter(r => this.getStarRating(r) === 3).length
    },

    negativeCount() {
      if (this.currentNetwork === 'facebook') {
        return this.reviews.filter(r => r.recommendationType === 'negative' || (!r.recommendationType && this.getStarRating(r) <= 2 && this.getStarRating(r) > 0)).length
      }
      return this.reviews.filter(r => this.getStarRating(r) <= 2).length
    },

    filteredReviews() {
      return this.reviews.filter(review => {
        const rating = this.getStarRating(review)
        const comment = (review.comment || '').toLowerCase()
        const reviewer = (review.reviewer?.displayName || '').toLowerCase()
        const query = (this.searchQuery || '').trim().toLowerCase()

        // Filter tabs
        if (this.activeFilter === 'unanswered' && review.reviewReply) return false
        if (this.activeFilter === 'replied' && !review.reviewReply) return false

        if (this.currentNetwork === 'facebook') {
          if (this.activeFilter === 'positive') {
            const isPos = review.recommendationType === 'positive' || (!review.recommendationType && rating >= 4)
            if (!isPos) return false
          }
          if (this.activeFilter === 'negative') {
            const isNeg = review.recommendationType === 'negative' || (!review.recommendationType && rating <= 2 && rating > 0)
            if (!isNeg) return false
          }
        } else {
          if (this.activeFilter === 'positive' && rating < 4) return false
          if (this.activeFilter === 'neutral' && rating !== 3) return false
          if (this.activeFilter === 'negative' && rating > 2) return false
        }

        // Search query
        if (query) {
          return comment.includes(query) || reviewer.includes(query)
        }

        return true
      })
    }
  },

  watch: {
    locationData: {
      immediate: true,
      handler(newVal) {
        if (newVal && (newVal.id || newVal.name || newVal.localPageId || this.locationId)) {
          this.loadReviews()
        }
      }
    },
    locationId(newVal) {
      if (newVal) {
        this.loadReviews()
      }
    }
  },

  mounted() {
    this.loadReviews()
  },

  methods: {
    ...mapActions('presenceManagement', [
      'fetchLocationReviews',
      'replyToLocationReview',
      'deleteLocationReviewReply',
      'generateAiReviewReply'
    ]),

    async loadReviews() {
      const targetId = this.targetId
      if (!targetId || this.loading) return
      this.loading = true
      try {
        const res = await this.fetchLocationReviews({ locationId: targetId })
        if (res && res.success) {
          this.reviews = (res.reviews || []).map(r => ({
            ...r,
            comment: this.cleanReviewText(r.comment),
            draft: r.reviewReply ? this.cleanReviewText(r.reviewReply.comment) : ''
          }))
          if (res.recommendationRate !== undefined) {
            this.serverRecommendationRate = res.recommendationRate
          }
        }
      } catch (err) {
        console.error('Error loading reviews:', err)
      } finally {
        this.loading = false
      }
    },

    getRecommendationPercentage(type) {
      const total = this.positiveCount + this.negativeCount
      if (total === 0) return 0
      const count = type === 'positive' ? this.positiveCount : this.negativeCount
      return Math.round((count / total) * 100)
    },

    getStarRating(review) {
      if (!review) return 0
      const starMap = { STAR_RATING_UNSPECIFIED: 0, ONE: 1, TWO: 2, THREE: 3, FOUR: 4, FIVE: 5 }
      return starMap[review.starRating] || Number(review.starRating) || 0
    },

    getStarIcon(star, review) {
      return star <= this.getStarRating(review) ? 'star' : 'star_border'
    },

    getAverageStarIcon(star) {
      return star <= Math.round(this.computedAverageRating) ? 'star' : 'star_border'
    },

    getStarCount(starRating) {
      return this.reviews.filter(r => this.getStarRating(r) === starRating).length
    },

    getStarPercentage(starRating) {
      if (!this.reviews || this.reviews.length === 0) return 0
      const count = this.getStarCount(starRating)
      return Math.round((count / this.reviews.length) * 100)
    },

    getInitials(name) {
      if (!name) return '?'
      const parts = name.trim().split(' ')
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
      return name.substring(0, 2).toUpperCase()
    },

    formatAvatarUrl(url) {
      if (!url || typeof url !== 'string') return ''
      let cleanUrl = url.trim().replace(/^http:\/\//i, 'https://')
      if (cleanUrl.includes('googleusercontent.com') && !cleanUrl.match(/=[swh]\d+/)) {
        cleanUrl = `${cleanUrl}=s80-c`
      }
      return cleanUrl
    },

    handleAvatarError(review) {
      const key = this.getReviewKey(review)
      if (key) {
        this.$set(this.avatarErrors, key, true)
      }
    },

    cleanReviewText(text) {
      if (!text || typeof text !== 'string') return text || ''
      const originalRegex = /\((?:Original|D'origine|Originale|Pôvodné|Oryginał|Ursprünglich)\)\s*:?\s*([\s\S]*)$/i
      const matchOriginal = text.match(originalRegex)
      if (matchOriginal && matchOriginal[1] && matchOriginal[1].trim()) {
        return matchOriginal[1].trim()
      }
      const translatedAfterRegex = /^([\s\S]+?)\s*\((?:Translated by Google|Traduit par Google|Traducido por Google|Tradotto da Google|Von Google übersetzt)\)/i
      const matchAfter = text.match(translatedAfterRegex)
      if (matchAfter && matchAfter[1] && matchAfter[1].trim()) {
        return matchAfter[1].trim()
      }
      const translatedStartRegex = /^\((?:Translated by Google|Traduit par Google|Traducido por Google|Tradotto da Google|Von Google übersetzt)\)\s*:?\s*([\s\S]*)$/i
      const matchStart = text.match(translatedStartRegex)
      if (matchStart && matchStart[1] && matchStart[1].trim()) {
        return matchStart[1].trim()
      }
      return text.trim()
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      try {
        const d = new Date(dateStr)
        return d.toLocaleDateString(this.$i18n?.locale || 'fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
      } catch (e) {
        return dateStr
      }
    },

    getReviewKey(review) {
      if (!review) return ''
      return review.reviewId || review.name || review.storyId || ''
    },

    getDraft(review) {
      const key = this.getReviewKey(review)
      if (this.replyDrafts && this.replyDrafts[key] !== undefined) {
        return this.replyDrafts[key]
      }
      return review.draft || ''
    },

    setDraft(review, val) {
      const key = this.getReviewKey(review)
      this.$set(this.replyDrafts, key, val)
      this.$set(review, 'draft', val)
      this.$forceUpdate()
    },

    // textarea dans un v-for : $refs renvoie un tableau
    getReplyTextarea(review) {
      return [].concat(this.$refs[`replyTextarea-${review.name}`] || [])[0]
    },

    openReviewTemplatePicker(review, action) {
      this.templateCaret = getCaret(this.getReplyTextarea(review), this.getDraft(review))
      this.templateAction = action
      this.templatePickerReviewName = review.name
    },

    getReviewTemplateSaveText(review) {
      return selectedOrAll(this.getDraft(review), this.templateCaret)
    },

    applyReviewTemplate(review, template) {
      const current = this.getDraft(review) || ''
      this.templatePickerReviewName = null
      if (template.type === 'affix') {
        this.setDraft(review, `${template.data.prefix || ''}${current}${template.data.suffix || ''}`)
        return
      }
      const { value, caret } = insertAtCaret(current, template.data.text, this.templateCaret)
      this.setDraft(review, value)
      this.$nextTick(() => restoreCaret(this.getReplyTextarea(review), caret))
    },

    toggleReply(review) {
      const key = this.getReviewKey(review)
      const idx = this.expandedReviewIds.indexOf(key)
      if (idx > -1) {
        this.expandedReviewIds.splice(idx, 1)
        if (this.replyingReviewIds.includes(review.name)) {
          this.replyingReviewIds = this.replyingReviewIds.filter(id => id !== review.name)
        }
      } else {
        this.expandedReviewIds.push(key)
      }
    },

    isReplyExpanded(review) {
      const key = this.getReviewKey(review)
      return this.expandedReviewIds.includes(key) || this.replyingReviewIds.includes(review.name)
    },

    startEditReply(review) {
      const key = this.getReviewKey(review)
      if (!this.expandedReviewIds.includes(key)) {
        this.expandedReviewIds.push(key)
      }
      if (!this.replyingReviewIds.includes(review.name)) {
        this.replyingReviewIds.push(review.name)
      }
      const initialVal = review.reviewReply ? review.reviewReply.comment : ''
      this.setDraft(review, initialVal)
    },

    cancelEditReply(review) {
      this.replyingReviewIds = this.replyingReviewIds.filter(id => id !== review.name)
    },

    async onSubmitReply(review) {
      const comment = this.getDraft(review)
      if (!comment || !comment.trim()) return

      const reviewKey = review.name || review.reviewId || review.storyId
      this.submittingIds.push(reviewKey)
      try {
        const res = await this.replyToLocationReview({
          locationId: this.targetId,
          reviewName: review.name,
          storyId: review.storyId || review.name,
          comment: comment.trim()
        })

        if (res && res.success) {
          const successText = this.currentNetwork === 'facebook'
            ? this.$t('dash.presenceManagement.reviews.saveSuccessTextFacebook')
            : this.$t('dash.presenceManagement.reviews.saveSuccessText')
          this.$vs.notify({
            title: this.$t('dash.presenceManagement.reviews.saveSuccessTitle'),
            text: successText,
            color: '#6a1f4c',
            position: 'top-right'
          })
          // Update review object locally
          this.$set(review, 'reviewReply', {
            comment: comment.trim(),
            updateTime: new Date().toISOString(),
            replyId: (res.reply && res.reply.replyId) || (review.reviewReply && review.reviewReply.replyId) || null
          })
          this.cancelEditReply(review)
        } else {
          const defaultFailed = this.currentNetwork === 'facebook'
            ? this.$t('dash.presenceManagement.reviews.saveFailedTextFacebook')
            : this.$t('dash.presenceManagement.reviews.saveFailedText')
          this.$vs.notify({
            title: this.$t('dash.presenceManagement.location.error.title'),
            text: (res && res.error) || defaultFailed,
            color: 'danger',
            position: 'top-right'
          })
        }
      } catch (err) {
        console.error('Error submitting review reply:', err)
      } finally {
        this.submittingIds = this.submittingIds.filter(id => id !== reviewKey)
      }
    },

    async confirmDeleteReply(review) {
      const confirmMsg = this.currentNetwork === 'facebook'
        ? this.$t('dash.presenceManagement.reviews.deleteConfirmFacebook')
        : this.$t('dash.presenceManagement.reviews.deleteConfirm')
      if (!confirm(confirmMsg)) return

      try {
        const res = await this.deleteLocationReviewReply({
          locationId: this.targetId,
          reviewName: review.name,
          replyId: review.reviewReply?.replyId
        })

        if (res && res.success) {
          const successText = this.currentNetwork === 'facebook'
            ? this.$t('dash.presenceManagement.reviews.deleteSuccessTextFacebook')
            : this.$t('dash.presenceManagement.reviews.deleteSuccessText')
          this.$vs.notify({
            title: this.$t('dash.presenceManagement.reviews.deleteSuccessTitle'),
            text: successText,
            color: '#6a1f4c',
            position: 'top-right'
          })
          this.$set(review, 'reviewReply', null)
          this.setDraft(review, '')
        }
      } catch (err) {
        console.error('Error deleting review reply:', err)
      }
    },

    async onGenerateAiReply(review) {
      if (!review) return
      const reviewKey = review.name || review.reviewId || review.storyId
      this.aiGeneratingIds.push(reviewKey)

      try {
        const res = await this.generateAiReviewReply({
          locationId: this.targetId,
          reviewComment: this.cleanReviewText(review.comment) || '',
          starRating: this.getStarRating(review),
          reviewerName: review.reviewer ? review.reviewer.displayName : '',
          recommendationType: review.recommendationType
        })

        if (res && res.success && res.replyText) {
          this.setDraft(review, res.replyText)
          this.$vs.notify({
            title: this.$t('dash.presenceManagement.reviews.aiGeneratedTitle'),
            text: this.$t('dash.presenceManagement.reviews.aiGeneratedText'),
            color: '#6a1f4c',
            position: 'top-right'
          })
        } else {
          this.$vs.notify({
            title: this.$t('dash.presenceManagement.location.error.title'),
            text: (res && res.error) || this.$t('dash.presenceManagement.reviews.aiGeneratedFailed'),
            color: 'danger',
            position: 'top-right'
          })
        }
      } catch (err) {
        console.error('Error generating AI review reply:', err)
      } finally {
        this.aiGeneratingIds = this.aiGeneratingIds.filter(id => id !== reviewKey)
      }
    }
  }
}
</script>

<style scoped>
.ler-view {
  padding: 8px 0;
}

.ler-loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #6a1f4c;
}

.ler-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid rgba(106, 31, 76, 0.15);
  border-top-color: #6a1f4c;
  border-radius: 50%;
  animation: lerSpin 0.8s linear infinite;
  margin-bottom: 12px;
}

@keyframes lerSpin {
  to { transform: rotate(360deg); }
}

.ler-empty-state {
  text-align: center;
  padding: 60px 20px;
  background: #fff;
  border: 1px dashed #e2d5ef;
  border-radius: 12px;
}

.ler-empty-icon {
  font-size: 48px;
  color: #c4a9d6;
  margin: 0 auto 12px auto;
  display: block;
  line-height: 1;
  text-align: center;
}

.ler-empty-title {
  font-size: 16px;
  font-weight: 700;
  color: #2e1937;
  margin-bottom: 6px;
}

.ler-empty-text {
  font-size: 13px;
  color: #7a6885;
}

/* Stats Banner */
.ler-stats-card {
  display: grid;
  grid-template-columns: 140px 1fr 120px;
  gap: 20px;
  align-items: center;
  background: #fff;
  /* border: 1px solid #e8dfee; */
  border-bottom: 1px solid #e8dfee;
  /* border-radius: 12px; */
  border-radius: 6px;
  padding: 20px 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(46, 25, 55, 0.04);
}

.ler-score-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  border-right: 1px solid #f0e6f5;
  padding-right: 16px;
}

.ler-score-val {
  font-size: 38px;
  font-weight: 800;
  color: #2e1937;
  line-height: 1;
}

.ler-stars-row {
  display: flex;
  gap: 2px;
  margin: 6px 0;
}

.ler-star-icon {
  font-size: 18px;
  color: #dadce0;
}

.ler-star-icon--filled {
  color: #fbbc04;
}

.ler-score-count {
  font-size: 12px;
  color: #7a6885;
}

.ler-distribution-col {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ler-dist-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
}

.ler-dist-label {
  display: flex;
  align-items: center;
  gap: 2px;
  width: 32px;
  font-weight: 600;
  color: #4a3852;
}

.ler-dist-star {
  font-size: 13px;
  color: #fbbc04;
}

.ler-dist-bar-bg {
  flex: 1;
  height: 8px;
  background: #f3edf7;
  border-radius: 4px;
  overflow: hidden;
}

.ler-dist-bar-fill {
  height: 100%;
  background: #fbbc04;
  border-radius: 4px;
  transition: width 0.3s ease;
}

.ler-dist-count {
  width: 24px;
  text-align: right;
  color: #7a6885;
}

.ler-rate-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  border-left: 1px solid #f0e6f5;
  padding-left: 16px;
}

.ler-rate-circle {
  width: 62px;
  height: 62px;
  border-radius: 50%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.ler-rate-circle::before {
  content: '';
  position: absolute;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  background: #fff;
}

.ler-rate-percent {
  position: relative;
  font-size: 12px;
  font-weight: 800;
  color: #6a1f4c;
}

.ler-rate-label {
  font-size: 11px;
  color: #7a6885;
  margin-top: 10px;
  text-align: center;
  font-weight: 600;
}

/* Toolbar & Filters */
.ler-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: 0 20px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.ler-filters-group {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.ler-filter-btn {
  padding: 8px 16px;
  border-radius: 20px;
  border: 1px solid #e2d5ef !important;
  background: #ffffff !important;
  color: #6a1f4c !important;
  color: #9a85a8 !important;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.ler-filter-btn:hover {
  background: #f7eff9 !important;
  border-color: #d1b8e4 !important;
  color: #6a1f4c !important;
}

.ler-filter-btn--active {
  background: #6a1f4c !important;
  border-color: #6a1f4c !important;
  color: #ffffff !important;
  font-weight: 600;
  box-shadow: 0 2px 6px rgba(106, 31, 76, 0.25);
}

.ler-filter-btn--active:hover {
  background: #58183f !important;
  color: #ffffff !important;
}

.ler-search-box {
  position: relative;
  min-width: 240px;
}

.ler-search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #9a85a8;
  font-size: 18px;
}

.ler-search-input {
  width: 100%;
  padding: 8px 12px 8px 34px;
  border: 1px solid #e2d5ef;
  border-radius: 8px;
  font-size: 13px;
  outline: none;
  transition: border 0.2s;
}

.ler-search-input:focus {
  border-color: #6a1f4c;
  box-shadow: 0 0 0 3px rgba(106, 31, 76, 0.1);
}

/* Reviews List */
.ler-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.ler-card {
  background: #fff;
  border: 1px solid #e8dfee;
  /* border-bottom: 1px solid #e8dfee; */
  /* border-radius: 12px; */
  border-radius: 6px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(46, 25, 55, 0.03);
  margin: 10px;
  margin-top: 0;
}

.ler-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.ler-reviewer-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ler-reviewer-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
}

.ler-reviewer-avatar-placeholder {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #6a1f4c;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
}

.ler-reviewer-meta {
  display: flex;
  flex-direction: column;
}

.ler-reviewer-name {
  font-size: 14px;
  font-weight: 700;
  color: #2e1937;
}

.ler-review-date {
  font-size: 12px;
  color: #8c7d96;
}

.ler-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.ler-status-badge i {
  font-size: 14px;
}

.ler-status-badge--replied {
  background: #e6f4ea;
  color: #137333;
}

.ler-status-badge--pending {
  background: #fef7e0;
  color: #b06000;
}

.ler-card-rating {
  display: flex;
  gap: 2px;
  margin-bottom: 8px;
}

.ler-card-comment {
  font-size: 14px;
  line-height: 1.5;
  color: #3c4043;
  margin-bottom: 12px;
}

.ler-card-comment--empty {
  color: #9a85a8;
}

/* Card Action Toggle Bar */
.ler-card-actions {
  display: flex;
  align-items: center;
  margin-top: 4px;
}

button.ler-btn-toggle-reply {
  display: inline-flex !important;
  align-items: center !important;
  gap: 6px !important;
  background: #ffffff !important;
  background-color: #ffffff !important;
  border: 1px solid #d8c2e6 !important;
  color: #6a1f4c !important;
  padding: 6px 14px !important;
  height: auto !important;
  min-height: 32px !important;
  border-radius: 6px !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  cursor: pointer !important;
  transition: all 0.2s ease !important;
}

button.ler-btn-toggle-reply span {
  text-transform: none !important;
  color: inherit !important;
}

button.ler-btn-toggle-reply .material-icons {
  font-size: 16px !important;
  color: inherit !important;
}

button.ler-btn-toggle-reply .ler-toggle-chevron {
  font-size: 18px !important;
  color: #9a85a8 !important;
  margin-left: 2px !important;
  transition: transform 0.2s ease !important;
}

button.ler-btn-toggle-reply:hover {
  background: #6a1f4c !important;
  background-color: #6a1f4c !important;
  border-color: #6a1f4c !important;
  color: #ffffff !important;
}

button.ler-btn-toggle-reply:hover span,
button.ler-btn-toggle-reply:hover .material-icons,
button.ler-btn-toggle-reply:hover .ler-toggle-chevron {
  color: #ffffff !important;
}

button.ler-btn-toggle-reply--expanded {
  background: #fbf5fc !important;
  background-color: #fbf5fc !important;
  border-color: #6a1f4c !important;
  color: #6a1f4c !important;
}

button.ler-btn-toggle-reply--expanded:hover {
  background: #6a1f4c !important;
  background-color: #6a1f4c !important;
  color: #ffffff !important;
}

.ler-card-reply-wrapper {
  margin-top: 8px;
}

/* Existing Reply Block */
.ler-existing-reply {
  background: #faf5fd;
  border-left: 4px solid #6a1f4c;
  border-radius: 0 8px 8px 0;
  padding: 14px 16px;
  margin-top: 12px;
}

.ler-reply-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.ler-reply-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 700;
  color: #6a1f4c;
}

.ler-reply-actions {
  display: flex;
  gap: 4px;

  .material-icons {
    font-size: 16px;
  }
}

.ler-btn-icon {
  background: transparent;
  border: none;
  /* color: #7a6885; */
  color: #6a1f4c !important;
  background-color: transparent !important;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}

.ler-btn-icon:hover {
  background: #eedef0 !important;
}

.ler-btn-icon--danger:hover {
  color: #c5221f !important;
}

.ler-reply-text {
  font-size: 13px;
  color: #2e1937;
  line-height: 1.4;
  margin: 0 0 6px 0;
}

.ler-reply-date {
  font-size: 11px;
  color: #8c7d96;
}

/* Reply Form Box */
.ler-reply-box {
  background: #fcf9fe;
  border: 1px solid #e8dfee;
  border-radius: 8px;
  padding: 14px 16px;
  margin-top: 12px;
}

.ler-reply-box-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.ler-reply-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 700;
  color: #6a1f4c;
}

.ler-btn-ai {
  display: flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #6a1f4c 0%, #9b3d75 100%);
  color: #fff;
  border: none;
  border-radius: 16px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
}

.ler-btn-ai:hover:not(:disabled) {
  opacity: 0.9;
}

.ler-btn-ai:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.ler-btn-ai ::v-deep span {
  text-transform: none !important;
}

.ler-reply-box-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.template-trigger-wrapper {
  position: relative;

  .template-picker {
    position: absolute;
    top: 100%;
    right: 0;
    z-index: 50;
    margin-top: 4px;
  }
}

.ler-btn-template {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #f5f6fa;
  color: #6a1f4c;
  border: 1px solid #e3e6ee;
  border-radius: 16px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.ler-btn-template:hover {
  background: #ecebf0;
}

.ler-spin {
  animation: lerSpin 1s linear infinite;
}

.ler-reply-textarea {
  width: 100%;
  min-height: 80px;
  background-color: #ffffff !important;
  color: #2e1937 !important;
  border: 1px solid #e2d5ef;
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.5;
  outline: none;
  resize: vertical;
  font-family: inherit;
  margin-bottom: 10px;
  transition: border 0.2s, box-shadow 0.2s;
}

.ler-reply-textarea::placeholder {
  color: #9e8dae;
}

.ler-reply-textarea:focus {
  border-color: #6a1f4c;
  box-shadow: 0 0 0 3px rgba(106, 31, 76, 0.1);
}

.ler-reply-box-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

.ler-btn-cancel {
  background: transparent;
  border: 1px solid #d1bfe3;
  color: #6a1f4c;
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.ler-btn-submit {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #6a1f4c;
  color: #fff;
  border: none;
  padding: 7px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  .material-icons {
    font-size: 16px;
  }
}

.ler-btn-submit:hover:not(:disabled) {
  background: #54173d;
}

.ler-btn-submit:disabled {
  background: #cbb8d4;
  cursor: not-allowed;
}

.ler-btn-submit ::v-deep span {
  text-transform: none !important;
}

.ler-spinner-small {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: lerSpin 0.8s linear infinite;
}

/* Facebook Recommendation Specific Styles */
.ler-score-col--fb {
  padding-right: 16px;
}

.ler-score-val--fb {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: #1877f2;
  font-size: 26px;
  font-weight: 700;
}

.ler-rec-hero-icon {
  font-size: 22px;
  color: #1877f2;
}

.ler-score-sublabel {
  font-size: 11px;
  font-weight: 600;
  color: #4a3852;
  text-align: center;
  margin: 6px 0 4px 0;
  max-width: 140px;
  line-height: 1.3;
}

.ler-distribution-col--fb {
  gap: 12px;
}

.ler-dist-label--fb {
  width: auto;
  min-width: 85px;
  font-size: 12px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.ler-dist-icon--pos {
  font-size: 15px;
  color: #2e7d32;
}

.ler-dist-icon--neg {
  font-size: 15px;
  color: #c62828;
}

.ler-dist-bar-fill--pos {
  background: #2e7d32;
}

.ler-dist-bar-fill--neg {
  background: #c62828;
}

.ler-card-recommendation {
  margin-bottom: 10px;
}

.ler-rec-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 700;
}

.ler-rec-pill i {
  font-size: 14px;
}

.ler-rec-pill--positive {
  background: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #c8e6c9;
}

.ler-rec-pill--negative {
  background: #ffebee;
  color: #c62828;
  border: 1px solid #ffcdd2;
}
</style>
